import React from 'react';
import PropTypes from 'prop-types';
import {fetchRecordJSON, getRecordDetailUrl, recordStatus, recordTime} from '../../lib/hydro-records';
import RecordDetail from './record-detail.jsx';
import styles from './records.css';

class HydroRecords extends React.Component {
    constructor (props) {
        super(props);
        this.state = {records: [], page: 1, hasMore: false, loading: true, error: '', rid: '', detail: null};
        this.generation = 0;
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleBack = this.handleBack.bind(this);
        this.handleRefresh = this.handleRefresh.bind(this);
        this.handlePrevious = this.handlePrevious.bind(this);
        this.handleNext = this.handleNext.bind(this);
        this.handleSelect = this.handleSelect.bind(this);
    }
    componentDidMount () {
        this.previousFocus = document.activeElement;
        this.closeButton.focus();
        document.addEventListener('keydown', this.handleKeyDown);
        this.loadList(1);
    }
    componentWillUnmount () {
        this.generation++;
        if (this.controller) this.controller.abort();
        document.removeEventListener('keydown', this.handleKeyDown);
        if (this.previousFocus?.isConnected) this.previousFocus.focus();
    }
    handleKeyDown (event) {
        if (event.key === 'Escape') {
            event.stopPropagation();
            this.props.onClose();
        }
        if (event.key === 'Tab') {
            const buttons = Array.from(this.dialog.querySelectorAll('button:not(:disabled)'));
            const first = buttons[0];
            const last = buttons[buttons.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        }
    }
    async request (url, state, apply) {
        if (this.controller) this.controller.abort();
        this.controller = new AbortController();
        const generation = ++this.generation;
        this.setState({...state, loading: true, error: ''});
        try {
            const result = await fetchRecordJSON(url, this.controller.signal);
            if (generation === this.generation) this.setState({...apply(result), loading: false});
        } catch (error) {
            if (generation === this.generation) this.setState({loading: false, error: error.message || '网络连接失败，请重试'});
        }
    }
    loadList (page) {
        const url = new URL(this.props.urls.list);
        url.searchParams.set('page', page);
        this.request(url.href, {page, rid: '', detail: null}, result => {
            if (!Array.isArray(result.rdocs)) throw new Error('提交记录返回格式异常，请重试');
            return {records: result.rdocs, hasMore: Boolean(result.hasMore)};
        });
    }
    loadDetail (rid) {
        this.request(getRecordDetailUrl(this.props.urls, rid), {rid, detail: null}, detail => ({detail}));
    }
    handleBack () {
        this.loadList(this.state.page);
    }
    handleRefresh () {
        if (this.state.rid) this.loadDetail(this.state.rid);
        else this.loadList(this.state.page);
    }
    handlePrevious () {
        this.loadList(this.state.page - 1);
    }
    handleNext () {
        this.loadList(this.state.page + 1);
    }
    handleSelect (event) {
        this.loadDetail(event.currentTarget.dataset.rid);
    }
    render () {
        const {records, page, hasMore, loading, error, rid, detail} = this.state;
        return (
            <div className={styles.overlay}>
                <div
                    aria-label="Scratch 提交记录"
                    aria-modal="true"
                    className={styles.dialog}
                    ref={element => {
                        this.dialog = element;
                    }}
                    role="dialog"
                >
                    <header className={styles.header}>
                        <div><h2>{rid ? '提交详情' : '提交记录'}</h2>
                            <p>{rid ? recordTime(rid) : '当前题目 · 我的 Scratch 提交'}</p></div>
                        <button
                            aria-label="关闭提交记录"
                            onClick={this.props.onClose}
                            ref={element => {
                                this.closeButton = element;
                            }}
                            type="button"
                        >{'×'}</button>
                    </header>
                    <div className={styles.toolbar}>
                        {rid && <button
                            onClick={this.handleBack}
                            type="button"
                        >{'← 返回记录'}</button>}
                        <button
                            disabled={loading}
                            onClick={this.handleRefresh}
                            type="button"
                        >{'刷新'}</button>
                    </div>
                    <div
                        aria-busy={loading}
                        className={styles.content}
                    >
                        {loading && <p role="status">{'正在加载…'}</p>}
                        {error && <p
                            className={styles.error}
                            role="alert"
                        >{error}{'（可点击刷新重试）'}</p>}
                        {!loading && !error && (rid ? detail && <RecordDetail result={detail} /> : (
                            <div>
                                {!records.length && <p className={styles.empty}>
                                    {page === 1 ? '还没有提交记录，完成作品后点击顶部“提交答题”吧。' : '这一页没有更多记录。'}
                                </p>}
                                {records.map(item => (
                                    <button
                                        className={styles.recordRow}
                                        data-rid={item._id}
                                        key={item._id}
                                        onClick={this.handleSelect}
                                        type="button"
                                    >
                                        <span>{recordTime(item._id)}</span>
                                        <strong className={item.status === 1 ? styles.accepted : styles.verdict}>
                                            {recordStatus(item.status)}
                                        </strong>
                                        <strong>{`${item.score ?? '—'} 分`}</strong>
                                        <span className={styles.link}>{'查看详情 →'}</span>
                                    </button>
                                ))}
                            </div>
                        ))}
                    </div>
                    {!rid && <footer className={styles.pagination}>
                        <button
                            disabled={loading || page === 1}
                            onClick={this.handlePrevious}
                            type="button"
                        >{'上一页'}</button>
                        <span>{`第 ${page} 页`}</span>
                        <button
                            disabled={loading || !hasMore || Boolean(error)}
                            onClick={this.handleNext}
                            type="button"
                        >{'下一页'}</button>
                    </footer>}
                </div>
            </div>
        );
    }
}
HydroRecords.propTypes = {urls: PropTypes.object.isRequired, onClose: PropTypes.func.isRequired};
export default HydroRecords;
